import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
  faPenToSquare,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";

export default function ReportsTable({ reports, forms }) {
  // 1. Локальный стейт, чтобы после удаления отчёта
  //    сразу перерисовать таблицу
  const [tableReports, setTableReports] = useState(reports);

  // Стейт для развёртывания рядов
  const [expandedRows, setExpandedRows] = useState({});

  // Стейт для модалки выбора формы
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Стейт для выбранной формы в Select
  const [selectedForm, setSelectedForm] = useState(null);

  // Стейт для ID периода, к которому добавляем форму
  const [activePeriodId, setActivePeriodId] = useState(null);

  // ** Новый стейт для модалки удаления отчёта **
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reportIdToDelete, setReportIdToDelete] = useState(null);

  // Открыть/закрыть ряд
  const toggleExpand = (rowKey) => {
    setExpandedRows((prevState) => ({
      ...prevState,
      [rowKey]: !prevState[rowKey],
    }));
  };

  // Открытие модалки выбора формы
  const handleOpenModal = (periodId) => {
    setActivePeriodId(periodId);
    setIsModalOpen(true);
  };

  // Закрытие модалки выбора формы
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedForm(null);
  };

  // Переход к редактированию или созданию формы
  const handleNavigate = () => {
    if (selectedForm) {
      // Проверяем, существует ли форма в текущем периоде
      const existingReport = tableReports
        .find((period) => period.period_id === activePeriodId)
        ?.reports.find((report) =>
          report.sub.some((form) => form.form_id === selectedForm.value)
        );

      if (existingReport) {
        // Если форма уже есть, переходим на редактирование
        window.location.href = `/reports/${existingReport.report_id}/edit`;
      } else {
        // Иначе создаём новую
        window.location.href = `/forms/${selectedForm.value}?period_id=${activePeriodId}`;
      }
    }
  };

  // 4. Удаление отчёта целиком (функция, которая вызывается при подтверждении)
  const handleDeleteReport = async () => {
    if (!reportIdToDelete) return;

    try {
      // Пример запроса на сервер (DELETE):
      await fetch(`/reports/${reportIdToDelete}`, {
        method: "DELETE",
      });

      // После успешного удаления на бэке — убираем отчёт из локального стейта
      setTableReports((prev) =>
        prev.map((period) => ({
          ...period,
          reports: period.reports.filter(
            (report) => report.report_id !== reportIdToDelete
          ),
        }))
      );

      // Закрываем модалку после удаления
      setIsDeleteModalOpen(false);
      setReportIdToDelete(null);
    } catch (error) {
      console.error("Ошибка при удалении отчёта:", error);
      // Можно показать уведомление об ошибке
    }
  };

  // Открыть модалку удаления отчёта
  const handleOpenDeleteModal = (reportId) => {
    setReportIdToDelete(reportId);
    setIsDeleteModalOpen(true);
  };

  // Закрыть модалку удаления (без удаления)
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setReportIdToDelete(null);
  };

  // Формируем варианты для селекта
  const formOptions = forms.map((form) => ({
    value: form.id,
    label: form.short_name,
  }));

  return (
    <div className="overflow-hidden rounded-lg border border-gray-300 shadow-md">
      <table className="table-fixed w-full border-collapse bg-white text-left text-sm text-gray-700">
        <thead className="bg-gradient-to-r from-blue-700 to-blue-800 text-white">
          <tr>
            <th className="w-16 px-6 py-4 font-medium"> </th>
            <th className="w-1/3 px-6 py-4 font-medium">Период</th>
            <th className="w-1/3 px-6 py-4 font-medium">Тип периода</th>
            <th className="w-1/6 px-6 py-4 font-medium"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 border-t border-gray-200">
          {tableReports.map((period) => (
            <React.Fragment key={period.period_id}>
              {/* Основная строка */}
              <tr
                className={`hover:bg-gray-50 ${
                  expandedRows[period.period_id] ? "align-top" : "align-middle"
                }`}
              >
                <td className="px-6 py-4">
                  {period.reports.some((report) => report.sub.length > 0) && (
                    <button
                      onClick={() => toggleExpand(period.period_id)}
                      className="text-blue-600 hover:text-blue-800 transition"
                    >
                      <FontAwesomeIcon
                        icon={expandedRows[period.period_id] ? faMinus : faPlus}
                        size="md"
                      />
                    </button>
                  )}
                </td>
                <th className="px-6 py-4 font-normal text-gray-900">
                  {period.period_date}
                </th>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                      period.period_status === "Открыт"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        period.period_status === "Открыт"
                          ? "bg-green-600"
                          : "bg-red-600"
                      }`}
                    ></span>
                    {period.period_status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {period.period_status !== "Закрыт" && (
                    <div className="flex justify-end gap-4">
                      <button
                        className="text-blue-600 hover:text-blue-800"
                        onClick={() => handleOpenModal(period.period_id)}
                      >
                        <FontAwesomeIcon icon={faPenToSquare} size="lg" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
              {/* Развёрнутый контент */}
              {expandedRows[period.period_id] && (
                <tr>
                  <td></td>
                  <td colSpan={3} className="px-6 py-4">
                    <div className="py-2">
                      {period.reports.map((report) => (
                        <div key={report.report_id} className="mb-2">
                          <div className="mb-1 flex items-center justify-between">
                            <div className="text-sm font-semibold text-gray-700">
                              Отчёт #{report.report_id}
                            </div>
                          </div>
                          {/* Список форм внутри отчёта */}
                          {report.sub &&
                            report.sub.map((form) => (
                              <div
                                key={form.form_id}
                                className="inline-block md:bg-blue-100 text-blue-700 md:px-3 py-2 rounded-full md:shadow-sm mr-2 mb-2"
                              >
                                <div className="flex items-center justify-between">
                                  <a
                                    href={`/reports/${report.report_id}/edit`}
                                    className="text-sm font-medium text-blue-600 hover:underline"
                                  >
                                    {form.short_name}
                                  </a>
                                  {/* Кнопка удалить отчёт */}
                                  <button
                                    className="ml-2 text-red-600 hover:text-red-800"
                                    onClick={() =>
                                      handleOpenDeleteModal(report.report_id)
                                    }
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrashAlt}
                                      size="sm"
                                    />
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {/* Модальное окно выбора формы */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full mx-auto md:w-1/3">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Выбор формы
            </h2>
            <Select
              options={formOptions}
              placeholder="Выберите форму"
              onChange={setSelectedForm}
              className="mb-4"
            />
            <div className="flex justify-end gap-4">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Отмена
              </button>
              <button
                onClick={handleNavigate}
                disabled={!selectedForm}
                className={`px-4 py-2 rounded-lg ${
                  selectedForm
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 text-gray-400 cursor-not-allowed"
                }`}
              >
                Перейти
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления отчёта */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full mx-auto md:w-1/3">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Удалить отчёт?
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Вы действительно хотите удалить отчёт #{reportIdToDelete}? Данное
              действие необратимо.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={handleCloseDeleteModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Отмена
              </button>
              <button
                onClick={handleDeleteReport}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
